import type { NavTone } from '@/components/layout/shell/types';
import type {
  HeldState,
  HomeGesture,
  ObserverEventKind,
  ObserverGesture,
  ObserverState,
} from './observerContext';

/*
  План событий помощника в оболочке (spec-3 §3, дом core). Одна таблица на всё, что зависит от вида
  события: приоритет в очереди, долгое состояние, жест дома на ядре в знаке и на выделении пункта,
  жест капли и финал тела у цели (этап D2), тон кольца, удержание, строка для читалки. Чат «жидкого
  помощника» меняет жесты и финалы здесь, а не в движке.

  Дом показывает верхнее состояние стопки: сбой > ждёт ответа > операция > покой. «Готово» — не
  состояние, а вспышка поверх стопки. Все жесты дома — готовые правила [data-slot='observer']
  [data-gesture] из motion.css; кольцо вокруг ядра (burst) играет сам знак.
*/

export interface EventPlan {
  /** Порядок в очереди: больше — раньше. */
  priority: number;
  /** Долгое состояние, которое событие ставит под своим ключом (держится до resolve). */
  holds?: HeldState;
  /** Вспышка поверх стопки (держится SUCCESS_HOLD_MS от события). «Готово» снимает и операцию своего ключа. */
  flash?: 'success';
  /** Жест дома на ядре в знаке; null — без жеста. */
  mark: HomeGesture | null;
  /** Жест дома на выделении пункта; дрожь на широкой таблетке не используется — залезла бы на соседей. */
  pill: HomeGesture | null;
  /** Жест капли у пункта меню (этап D2). */
  drop: ObserverGesture | null;
  /** Финал тела у цели на холсте (этап D2): одна волна от камня или разлив «готово». */
  finale: 'wave' | 'success' | null;
  /** Летит ли вообще: сбой — никогда, показывает только дом. */
  flies: boolean;
  /** Тон кольца на цели (вариант -fg — контраст не ниже 3:1 в обеих темах). */
  ring: 'brand' | 'success';
  /** Удержание у цели, мс. */
  holdMs: number;
  /** Строка для читалки, если событие не принесло своей. */
  say: string;
  /** Схлопнутые события: «3 новых уведомления» — формы для 1, 2–4, 5+. */
  many?: readonly [string, string, string];
  /** Срочность строки для читалки. */
  live: 'polite' | 'assertive';
}

export const EVENT_PLAN: Record<ObserverEventKind, EventPlan> = {
  notification: {
    priority: 1,
    mark: 'burst',
    pill: 'pulse',
    drop: 'bounce',
    finale: 'wave',
    flies: true,
    ring: 'brand',
    holdMs: 1200,
    say: 'Новое уведомление',
    many: ['новое уведомление', 'новых уведомления', 'новых уведомлений'],
    live: 'polite',
  },
  message: {
    priority: 1,
    mark: 'burst',
    pill: 'pulse',
    drop: 'nod',
    finale: 'wave',
    flies: true,
    ring: 'brand',
    holdMs: 1200,
    say: 'Новое сообщение',
    many: ['новое сообщение', 'новых сообщения', 'новых сообщений'],
    live: 'polite',
  },
  done: {
    priority: 2,
    flash: 'success',
    mark: 'flash',
    pill: 'pulse',
    drop: 'flash',
    finale: 'success',
    flies: true,
    ring: 'success',
    holdMs: 1400,
    say: 'Операция завершена',
    live: 'polite',
  },
  ask: {
    priority: 3,
    holds: 'asking',
    // Дома отдельного жеста нет: включается ритм ожидания.
    mark: null,
    pill: null,
    drop: 'nod',
    finale: 'wave',
    flies: true,
    ring: 'brand',
    holdMs: 1600,
    say: 'Нужен ответ',
    live: 'polite',
  },
  error: {
    priority: 4,
    holds: 'error',
    mark: 'shiver',
    // У выделения — только скачок цвета.
    pill: null,
    drop: null,
    finale: null,
    flies: false,
    ring: 'brand',
    holdMs: 0,
    say: 'Сбой',
    live: 'assertive',
  },
  update: {
    priority: 0,
    holds: 'update',
    mark: null,
    pill: null,
    drop: null,
    finale: null,
    flies: false,
    ring: 'brand',
    holdMs: 0,
    say: 'Идёт операция',
    live: 'polite',
  },
};

/**
 * Площадка /design/observer (дом orbit): своё тело отзывается жестом и состоянием на 2,6 с — как было.
 * «Нужен ответ» там не показывают; кивок — на случай, если покажут.
 */
export const ORBIT_PLAN: Record<ObserverEventKind, { gesture: ObserverGesture; state: ObserverState }> = {
  notification: { gesture: 'bounce', state: 'idle' },
  message: { gesture: 'nod', state: 'idle' },
  error: { gesture: 'shiver', state: 'error' },
  done: { gesture: 'flash', state: 'success' },
  update: { gesture: 'pulse', state: 'update' },
  ask: { gesture: 'nod', state: 'idle' },
};

export const PRIORITY = Object.fromEntries(
  Object.entries(EVENT_PLAN).map(([k, p]) => [k, p.priority]),
) as Record<ObserverEventKind, number>;

/** Старшинство долгих состояний в стопке: дом показывает верхнее. */
export const STATE_RANK: Record<HeldState, number> = {
  error: 4,
  asking: 3,
  update: 2,
  thinking: 1,
  writing: 1,
  download: 1,
  upload: 1,
};

/** Тон дома по состоянию (R38): «ждёт ответа» — коралл, его несёт ритм, а не новый цвет (R41: один коралл). */
export const STATE_TONE: Record<ObserverState, NavTone> = {
  idle: 'brand',
  thinking: 'brand',
  writing: 'info',
  download: 'info',
  upload: 'info',
  update: 'info',
  asking: 'brand',
  success: 'success',
  error: 'danger',
};

export function toneOf(state: ObserverState): NavTone {
  return STATE_TONE[state];
}

/** Верхнее состояние стопки. */
export function topOf(holds: Readonly<Record<string, HeldState>>): ObserverState {
  let top: ObserverState = 'idle';
  let rank = 0;
  for (const s of Object.values(holds)) {
    if (STATE_RANK[s] > rank) {
      rank = STATE_RANK[s];
      top = s;
    }
  }
  return top;
}

/** Длительность жеста дома, мс: столько очередь ждёт до следующей реакции (кадры — motion.css, кольцо — знак). */
export const HOME_GESTURE_MS: Record<HomeGesture, number> = {
  bounce: 620,
  shake: 520,
  shiver: 620,
  flash: 520,
  nod: 480,
  pulse: 900,
  burst: 700,
};

/* ----- Темп очереди (spec-3 §4–§5) ----- */

/** Не чаще одного полёта за столько, мс; второе событие раньше — жест дома. */
export const FLIGHT_GAP_MS = 6000;
/** Тот же ключ за столько, мс, — один показ, число растёт. */
export const COALESCE_MS = 1500;
/** Событие, прождавшее дольше, реакции не получает: признаки уже на месте. */
export const QUEUE_MAX_MS = 10000;
/** Как часто очередь заглядывает, свободен ли дом и человек. */
export const DEFER_TICK_MS = 250;
/** Пауза после показа страницы до первого полёта. */
export const SHOW_GRACE_MS = 600;
/** Вспышка «готово» держится столько от события, потом дом уходит к верхнему долгому состоянию. */
export const SUCCESS_HOLD_MS = 2600;
/** Кольцо на видимой цели в режиме «На месте». */
export const RING_MS = 1900;
/** Между двумя реакциями дома, мс: жесты не сливаются в один. */
export const REACT_GAP_MS = 160;

/* ----- Якоря ----- */

/** Цепочка адресов события. */
export function anchorIds(anchorId: string | string[] | undefined): string[] {
  if (!anchorId) return [];
  return Array.isArray(anchorId) ? anchorId : [anchorId];
}

/**
 * Первый подходящий якорь цепочки: get — элементы по адресу (у адреса их может быть несколько: строка на
 * главной и шапка вкладки), fits — годится ли (виден, не пункт меню для кольца и т. п.).
 */
export function pickAnchor(
  ids: string | string[] | undefined,
  get: (id: string) => readonly HTMLElement[],
  fits: (el: HTMLElement, id: string) => boolean,
): { id: string; el: HTMLElement } | null {
  for (const id of anchorIds(ids)) {
    for (const el of get(id)) if (fits(el, id)) return { id, el };
  }
  return null;
}

/**
 * Якорь виден: прямоугольник ненулевой и в окне, а точка в его центре — его собственная (не закрыта
 * слоем, не обрезана прокруткой или спрятанным меню). Маршрут курьера (этап D2) проверяет больше.
 */
export function isAnchorVisible(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return false;
  if (r.right <= 0 || r.bottom <= 0 || r.left >= window.innerWidth || r.top >= window.innerHeight) {
    return false;
  }
  const x = Math.min(Math.max(r.left + r.width / 2, 0), window.innerWidth - 1);
  const y = Math.min(Math.max(r.top + r.height / 2, 0), window.innerHeight - 1);
  const hit = document.elementFromPoint(x, y);
  return !!hit && (hit === el || el.contains(hit));
}

/** Поля, где печатают: фокус в них — человек занят, путь к событию ждёт (spec-3 §5). */
const NOT_TYPING = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
  'image',
]);

export function isTypingTarget(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  if (el.getAttribute('role') === 'combobox') return true;
  return el instanceof HTMLInputElement && !NOT_TYPING.has(el.type);
}

/* ----- Строка для читалки ----- */

/** Русская форма числа: 1 уведомление, 3 уведомления, 5 уведомлений. */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return forms[1];
  return forms[2];
}

/** Что прочтёт читалка: строка события, а при схлопывании — число («3 новых уведомления»). */
export function liveText(kind: ObserverEventKind, label: string | undefined, n: number): string {
  const plan = EVENT_PLAN[kind];
  if (n > 1 && plan.many) return `${n} ${plural(n, plan.many)}`;
  return label ?? plan.say;
}
